import BigNumber from 'bignumber.js';

import { msToDate } from '#/utils/date';
import { AsyncResp, BaseReq, requestApi } from '#/api/fetch-api';

import { UnixTimeMs } from './types';

type PointsPool = 'Tier1' | 'Tier2';

export const AUXILIARY_PROGRAMS = [
  'Referral',
  'Affiliate',
  'TVL',
  'Discretionary',
] as const;
export type AuxiliaryProgram = typeof AUXILIARY_PROGRAMS[number];

export interface RawPointsLPEntry {
  readonly updated_at: UnixTimeMs;
  readonly market: string;
  readonly program: 'LiquidityProvider';
  readonly pool: PointsPool;
  readonly quote_quality: string;
  readonly maker_volume_score: string;
  readonly wallet_score: string;
  readonly total_market_score: string;
  readonly score_share: string;
  readonly market_pool_share: string;
  readonly total_accrued_points: string;
  readonly total_distributed_points: string;
}

export interface RawPointsTraderEntry {
  readonly updated_at: UnixTimeMs;
  readonly market: string;
  readonly program: 'Trader';
  readonly pool: PointsPool;
  readonly wallet_score: string;
  readonly total_market_score: string;
  readonly score_share: string;
  readonly market_pool_share: string;
  readonly total_accrued_points: string;
  readonly total_distributed_points: string;
}

export interface RawPointsAuxiliaryEntry {
  readonly updated_at: UnixTimeMs;
  readonly program: AuxiliaryProgram;
  readonly total_accrued_points: string;
}

export interface PointsLPEntry
  extends Omit<
    RawPointsLPEntry,
    | 'updated_at'
    | 'quote_quality'
    | 'maker_volume_score'
    | 'wallet_score'
    | 'total_market_score'
    | 'score_share'
    | 'market_pool_share'
    | 'total_accrued_points'
    | 'total_distributed_points'
  > {
  readonly updated_at: Date;
  readonly quote_quality: BigNumber;
  readonly maker_volume_score: BigNumber;
  readonly wallet_score: BigNumber;
  readonly total_market_score: BigNumber;
  readonly score_share: BigNumber;
  readonly market_pool_share: BigNumber;
  readonly total_accrued_points: BigNumber;
  readonly total_distributed_points: BigNumber;
}

export interface PointsTraderEntry
  extends Omit<
    RawPointsTraderEntry,
    | 'updated_at'
    | 'wallet_score'
    | 'total_market_score'
    | 'score_share'
    | 'market_pool_share'
    | 'total_accrued_points'
    | 'total_distributed_points'
  > {
  readonly updated_at: Date;
  readonly wallet_score: BigNumber;
  readonly total_market_score: BigNumber;
  readonly score_share: BigNumber;
  readonly market_pool_share: BigNumber;
  readonly total_accrued_points: BigNumber;
  readonly total_distributed_points: BigNumber;
}

export interface PointsAuxiliaryEntry
  extends Omit<RawPointsAuxiliaryEntry, 'updated_at' | 'total_accrued_points'> {
  readonly updated_at: Date;
  readonly total_accrued_points: BigNumber;
}

interface PointsLPReq extends BaseReq {
  readonly market: string;
}

interface PointsLPRawResp {
  readonly results: readonly RawPointsLPEntry[];
}

export interface PointsLPResp {
  readonly results: readonly PointsLPEntry[];
}

export async function getPointsLP(req: PointsLPReq): AsyncResp<PointsLPResp> {
  const { signal, market } = req;

  const resp = await requestApi<PointsLPRawResp>({
    signal,
    method: 'GET',
    url: `/points_data/${market}/LiquidityProvider`,
  });

  if (!resp.ok) {
    return resp;
  }

  return {
    ...resp,
    data: { results: resp.data.results.map(processPointsLP) },
  };
}

interface PointsTraderReq extends BaseReq {
  readonly market: string;
}

interface PointsTraderRawResp {
  readonly results: readonly RawPointsTraderEntry[];
}

export interface PointsTraderResp {
  readonly results: readonly PointsTraderEntry[];
}

export async function getPointsTrader(
  req: PointsTraderReq,
): AsyncResp<PointsTraderResp> {
  const { signal, market } = req;

  const resp = await requestApi<PointsTraderRawResp>({
    signal,
    method: 'GET',
    url: `/points_data/${market}/Trader`,
  });

  if (!resp.ok) {
    return resp;
  }

  return {
    ...resp,
    data: { results: resp.data.results.map(processPointsTrader) },
  };
}

interface PointsAuxiliaryReq extends BaseReq {
  readonly program: AuxiliaryProgram;
}

interface PointsAuxiliaryRawResp {
  readonly results: readonly [] | [RawPointsAuxiliaryEntry];
}

export interface PointsAuxiliaryResp {
  readonly results: readonly [] | [PointsAuxiliaryEntry];
}

export async function getPointsAuxiliary(
  req: PointsAuxiliaryReq,
): AsyncResp<PointsAuxiliaryResp> {
  const { signal, program } = req;

  const resp = await requestApi<PointsAuxiliaryRawResp>({
    signal,
    method: 'GET',
    url: `/points_data/ALL/${program}`,
  });

  if (!resp.ok) {
    return resp;
  }

  const results = resp.data.results.map(
    processPointsAuxiliary,
  ) as PointsAuxiliaryResp['results'];

  return {
    ...resp,
    data: {
      results,
    },
  };
}

export function isRawPointsLPEntry(entry: {
  program: string;
}): entry is RawPointsLPEntry {
  return entry.program === 'LiquidityProvider';
}

export function isRawPointsTraderEntry(entry: {
  program: string;
}): entry is RawPointsTraderEntry {
  return entry.program === 'Trader';
}

export function isRawPointsAuxiliaryEntry(entry: {
  program: string;
}): entry is RawPointsAuxiliaryEntry {
  return AUXILIARY_PROGRAMS.includes(entry.program as AuxiliaryProgram);
}

export function processPointsLP(raw: RawPointsLPEntry): PointsLPEntry {
  return {
    ...raw,
    updated_at: msToDate(raw.updated_at),
    quote_quality: new BigNumber(raw.quote_quality),
    maker_volume_score: new BigNumber(raw.maker_volume_score),
    wallet_score: new BigNumber(raw.wallet_score),
    total_market_score: new BigNumber(raw.total_market_score),
    score_share: new BigNumber(raw.score_share),
    market_pool_share: new BigNumber(raw.market_pool_share),
    total_accrued_points: new BigNumber(raw.total_accrued_points),
    total_distributed_points: new BigNumber(raw.total_distributed_points),
  };
}

export function processPointsTrader(
  raw: RawPointsTraderEntry,
): PointsTraderEntry {
  return {
    ...raw,
    updated_at: msToDate(raw.updated_at),
    wallet_score: new BigNumber(raw.wallet_score),
    total_market_score: new BigNumber(raw.total_market_score),
    score_share: new BigNumber(raw.score_share),
    market_pool_share: new BigNumber(raw.market_pool_share),
    total_accrued_points: new BigNumber(raw.total_accrued_points),
    total_distributed_points: new BigNumber(raw.total_distributed_points),
  };
}

export function processPointsAuxiliary(
  raw: RawPointsAuxiliaryEntry,
): PointsAuxiliaryEntry {
  return {
    updated_at: msToDate(raw.updated_at),
    program: raw.program,
    total_accrued_points: new BigNumber(raw.total_accrued_points),
  };
}
